import { useQuery } from '@tanstack/react-query'
import { compact } from 'lodash'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import OnboardingCopyInProgressBannerSrc from '../../images/onboarding-copy-in-progress-banner.svg'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { Icon, IconName } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { SelectList } from '../common/SelectList'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { WorkflowPage } from '../workflows/WorkflowPage'

export const SetupOnboardingCopyDataPage = () => {
    const navigate = useNavigate()
    const me = PSApplication.me()

    const { advanceOnboardingState, setDidCopyFromOtherAccount } = useOnboardingContext()

    const { data: associatedAccountUsers } = useQuery({
        queryKey: ['associated-account-users'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchRoles('copy-data')
            if (!result.ok) {
                throw result.error
            }
            return result.value
        },
    })

    const [step, setStep] = useState<
        'choice' | 'account-picker' | 'info-picker' | 'copy-in-progress'
    >('choice')

    const [selectedChoiceID, setSelectedChoiceID] = useState<string | null>(null)
    const [selectedAccountUserID, setSelectedAccountUserID] = useState<string | null>(null)
    const [selectedCopyableDataIDsBySectionTitle, setSelectedCopyableDataIDsBySectionTitle] =
        useState<Record<string, string[]>>({})
    const [isCopying, setIsCopying] = useState(false)

    const selectedChoiceItem = choiceItems.find((item) => item.id === selectedChoiceID)
    const handleSelectChoice = (items: typeof choiceItems) => {
        setSelectedChoiceID(items[0]?.id ?? null)
    }

    const associatedAccountItems = associatedAccountUsers?.map((user) => ({
        id: user.id ?? '',
        title: user.entity ?? user.name ?? '',
        tertiaryView: <ContactAvatar user={user} />,
        user,
    }))
    const selectedAssociatedAccountItem = associatedAccountItems?.find(
        (item) => item.id === selectedAccountUserID
    )
    const selectedAccountUser = selectedAssociatedAccountItem?.user
    const handleSelectAssociatedAccountItem = (
        items: NonNullable<typeof associatedAccountItems>
    ) => {
        setSelectedAccountUserID(items[0]?.id ?? null)
    }

    const copyableDataItemSections =
        selectedAccountUser !== undefined
            ? generateCopyableDataItemSections(me, selectedAccountUser)
            : []
    const handleSelectCopyableDataItem = (
        sectionTitle: string,
        items: (typeof copyableDataItemSections)[number]['items']
    ) => {
        setSelectedCopyableDataIDsBySectionTitle((previous) => ({
            ...previous,
            [sectionTitle]: items.map((item) => item.id),
        }))
    }

    const canAdvance = ((): boolean => {
        if (isCopying) {
            return false
        }

        switch (step) {
            case 'choice': {
                return selectedChoiceID !== null
            }
            case 'account-picker': {
                return selectedAccountUserID !== null
            }
            case 'info-picker': {
                return true
            }
            case 'copy-in-progress': {
                return true
            }
        }
    })()

    const handleNext = async () => {
        if (step === 'choice') {
            if (selectedChoiceID === 'start-from-scratch') {
                setDidCopyFromOtherAccount(false)
                advanceOnboardingState()
                return
            }

            if (associatedAccountItems !== undefined && associatedAccountItems.length === 1) {
                setSelectedAccountUserID(associatedAccountItems[0]?.id ?? null)
                setStep('info-picker')
            }
            else {
                setStep('account-picker')
            }
        }
        else if (step === 'account-picker') {
            setStep('info-picker')
        }
        else if (step === 'info-picker') {
            if (selectedAccountUserID === null) {
                return
            }

            setDidCopyFromOtherAccount(true)

            setIsCopying(true)
            await RecordFactory.getInstance().sendOnboardingCopyData(
                selectedAccountUserID,
                Object.values(selectedCopyableDataIDsBySectionTitle).flat()
            )
            setIsCopying(false)

            setStep('copy-in-progress')
        }
    }

    const handleBack = () => {
        if (step === 'choice') {
            navigate('/')
        }
        else if (step === 'account-picker') {
            setStep('choice')
        }
        else if (step === 'info-picker') {
            setStep('account-picker')
        }
    }

    useEffect(() => {
        if (associatedAccountUsers === undefined) {
            return
        }

        if (associatedAccountUsers.length === 0) {
            setDidCopyFromOtherAccount(false)
            advanceOnboardingState()
        }
    }, [associatedAccountUsers, advanceOnboardingState, setDidCopyFromOtherAccount])

    return (
        <WorkflowPage
            title={
                isCopying || step === 'copy-in-progress'
                    ? undefined
                    : step === 'choice'
                      ? 'How do you want to get setup?'
                      : step === 'account-picker'
                        ? 'Select the account to copy'
                        : 'Select the information to copy'
            }
            onClose={isCopying ? undefined : () => navigate('/')}
            onBack={handleBack}
            backButtonTitle={isCopying || step === 'copy-in-progress' ? undefined : 'Back'}
            footerContent={
                step === 'copy-in-progress' ? undefined : (
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={!canAdvance}
                            onClick={handleNext}
                        >
                            Next
                        </Button>
                    </div>
                )
            }
        >
            {isCopying ? (
                <div className="flex h-full w-full flex-col items-center justify-center">
                    <Spinner />
                    <Heading size="md" className="mt-6 mb-4 text-gray-700">
                        Copying information from your account...
                    </Heading>
                    <Paragraph size="md" className="text-gray-600">
                        This may take a few seconds
                    </Paragraph>
                </div>
            ) : (
                <>
                    {step === 'info-picker' && (
                        <Paragraph size="md" className="mt-4 text-gray-600">
                            You can always change this later in settings
                        </Paragraph>
                    )}

                    {step !== 'copy-in-progress' ? (
                        <WorkflowPage.Card className="mt-12">
                            {step === 'choice' ? (
                                <div>
                                    <SelectList
                                        selectionType="single"
                                        items={choiceItems}
                                        selectedItems={
                                            selectedChoiceItem !== undefined
                                                ? [selectedChoiceItem]
                                                : []
                                        }
                                        onSelectItems={handleSelectChoice}
                                    />

                                    <div className="mt-3 -mb-4 flex items-center justify-center">
                                        <a
                                            href="https://pocketsuite.io"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <Button intent="tertiary" size="small">
                                                Learn more <Icon icon="externalLink.small" />
                                            </Button>
                                        </a>
                                    </div>
                                </div>
                            ) : step === 'account-picker' ? (
                                <SelectList
                                    selectionType="single"
                                    items={associatedAccountItems ?? []}
                                    selectedItems={
                                        selectedAssociatedAccountItem !== undefined
                                            ? [selectedAssociatedAccountItem]
                                            : []
                                    }
                                    onSelectItems={handleSelectAssociatedAccountItem}
                                />
                            ) : (
                                <div className="flex flex-col items-stretch justify-start gap-y-6">
                                    {copyableDataItemSections.map((section) => (
                                        <div key={section.title}>
                                            <Paragraph size="md" className="text-gray-600">
                                                {section.title}
                                            </Paragraph>

                                            <div className="mt-4">
                                                <SelectList
                                                    selectionType="multi"
                                                    items={section.items}
                                                    selectedItems={section.items.filter((item) =>
                                                        selectedCopyableDataIDsBySectionTitle[
                                                            section.title
                                                        ]?.includes(item.id)
                                                    )}
                                                    onSelectItems={(items) =>
                                                        handleSelectCopyableDataItem(
                                                            section.title,
                                                            items
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </WorkflowPage.Card>
                    ) : (
                        <div className="flex h-full items-center justify-center">
                            <Card size="md" className="p-0">
                                <img src={OnboardingCopyInProgressBannerSrc} />
                                <div className="flex flex-col items-stretch justify-center p-8">
                                    <Heading size="xl" className="text-center">
                                        Data copy in progress
                                    </Heading>
                                    <Paragraph
                                        size="md"
                                        className="mt-4 mb-10 text-center text-gray-600"
                                    >
                                        You will be notified at {me.email} when your data is copied
                                    </Paragraph>

                                    <Button
                                        intent="primary"
                                        onClick={() => advanceOnboardingState()}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    )}
                </>
            )}
        </WorkflowPage>
    )
}

const choiceItems = [
    {
        id: 'copy',
        title: 'Copy from existing account',
        subtitle:
            'Copy some or all of the items, settings, clients, and more from an existing account',
        tertiaryView: <Icon icon="copy.large" background="round" />,
    },
    {
        id: 'start-from-scratch',
        title: 'Start from scratch',
        subtitle: 'Use our guided onboarding to easily setup a new business',
        tertiaryView: <Icon icon="plus.large" background="round" />,
    },
]

const generateCopyableDataItemSections = (
    currentUser: User,
    sourceUser: User
): { title: string; items: { id: string; title: string; tertiaryView: JSX.Element }[] }[] => {
    // This function checks that the feature is enabled for both the current and source user
    const isEnabled = (callback: (user: User) => boolean | undefined) =>
        !!callback(currentUser) && !!callback(sourceUser)

    const sections: { title: string; items: [string, string, IconName][] }[] = [
        {
            title: 'Services',
            items: compact([
                ['services', 'Services', 'tag'],
                isEnabled((user) => user.allowPackage) && ['packages', 'Packages', 'package2'],
                isEnabled((user) => user.allowSubscription) && [
                    'subscriptions',
                    'Subscriptions',
                    'refresh',
                ],
                isEnabled((user) => user.allowClass) && ['classes', 'Classes', 'student'],
                isEnabled((user) => user.allowReservation) && [
                    'reservations',
                    'Reservations',
                    'calendarClock',
                ],
                ['categories', 'Categories', 'grid'],
                isEnabled((user) => user.allowProduct) && ['products', 'Products', 'package'],
                isEnabled((user) => user.allowGift) && ['gifts', 'Gifts', 'gift'],
            ]),
        },
        {
            title: 'Settings',
            items: compact([
                ['features', 'Features', 'form'],
                ['profile_settings', 'Profile', 'user2'],
                ['booking_settings', 'Scheduling', 'windowClock'],
                ['notification_settings', 'Notifications', 'bell'],
                ['memo_settings', 'Default memos', 'layers'],
                ['cancellation_settings', 'Cancellations', 'calendarMinus'],
                isEnabled((user) => user.allowProduct) && [
                    'order_settings',
                    'Product orders',
                    'truck',
                ],
                isEnabled((user) => user.allowReservation) && [
                    'actions',
                    'Smart tasks',
                    'checkmark',
                ],
                isEnabled((user) => user.allowReservation) && [
                    'projects',
                    'Smart projects',
                    'checkmark',
                ],
            ]),
        },
        {
            title: 'Marketing suite',
            items: compact([
                isEnabled((user) => user.allowLink) && ['links', 'Suite link', 'monitor'],
                isEnabled((user) => user.allowTemplate) && [
                    'templates',
                    'Saved messages',
                    'messageCheckmark',
                ],
                isEnabled((user) => user.allowRecord) && ['types', 'Forms', 'field'],
                isEnabled((user) => user.allowContract) && ['contracts', 'Contracts', 'pen'],
                isEnabled((user) => user.allowField) && ['fields', 'Client fields', 'settings'],
                isEnabled((user) => user.allowTrigger) && ['triggers', 'Smart campaign', 'flag'],
            ]),
        },
        {
            title: 'Clients & team members',
            items: compact([
                ['clients', 'Client list', 'user3'],
                isEnabled((user) => user.allowStaffing) && [
                    'payees',
                    'Team members',
                    'employeeCard',
                ],
            ]),
        },
    ]

    return sections
        .filter((section) => section.items.length > 0)
        .map((section) => ({
            ...section,
            items: section.items.map(([id, title, iconName]) => ({
                id,
                title,
                tertiaryView: <Icon icon={`${iconName}.large`} background="round" />,
            })),
        }))
}
